/**
 * Randomness Utilities
 *
 * Provides cryptographically secure random number generation
 * for fair block selection in the mining simulation.
 *
 * Uses Web Crypto API for true randomness (not Math.random).
 * This module is completely standalone and can be easily tested.
 */

/**
 * Generate a cryptographically secure random integer
 *
 * @param {number} min - Minimum value (inclusive)
 * @param {number} max - Maximum value (inclusive)
 * @returns {number} Random integer between min and max
 */
export function getSecureRandomInt(min, max) {
    const range = max - min + 1;
    const bytesNeeded = Math.ceil(Math.log2(range) / 8);
    const maxValue = Math.pow(256, bytesNeeded);
    const randomBytes = new Uint8Array(bytesNeeded);

    let randomValue;
    do {
        crypto.getRandomValues(randomBytes);
        randomValue = 0;
        for (let i = 0; i < bytesNeeded; i++) {
            randomValue = randomValue * 256 + randomBytes[i];
        }
    } while (randomValue >= maxValue - (maxValue % range));

    return min + (randomValue % range);
}

/**
 * Select a random block number from 1 to 25
 *
 * @returns {number} Block number (1-25)
 */
export function selectRandomBlock() {
    return getSecureRandomInt(1, 25);
}

/**
 * Check if motherlode was hit (1 in 625 chance)
 *
 * @returns {boolean} True if motherlode was hit
 */
export function checkMotherlode() {
    const roll = getSecureRandomInt(1, 625);
    return roll === 1; // 1 in 625 chance
}

/**
 * Generate random transaction delay (for simulation)
 *
 * @param {number} min - Minimum delay in ms
 * @param {number} max - Maximum delay in ms
 * @returns {number} Random delay in ms
 */
export function getRandomDelay(min = 1000, max = 3000) {
    return getSecureRandomInt(min, max);
}

/**
 * Simulate transaction success/failure
 *
 * @param {number} successRate - Success rate (0-1), default 0.95
 * @returns {boolean} True if transaction succeeds
 */
export function simulateTransactionSuccess(successRate = 0.95) {
    const roll = getSecureRandomInt(1, 100);
    return roll <= successRate * 100;
}
